import { StyledTableLink } from '@common/Dependency/styled';
import { useOrchestrationFabrics } from '@common/Fabric/hooks';
import { useOrchestrationMultiFabricQuery } from '@common/Fabric/OrchestrationFabric/api';
import { useOrchestrationHistoryApis } from '@common/Fabric/OrchestrationFabric/useOrchestrationHistoryApis';
import { useUserSQLProjects } from '@common/projectHooks/useUserSQLProjects';
import { Entity } from '@common/types/Entity';
import { getSqlIDEUrl, Private_Routes } from '@common/url';
import { useVerticalAppFlag } from '@common/VerticalApps/VerticalAppContext';
import { ArrowUpRightIcon, SearchSmIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Table } from '@prophecy/ui-v3/Table';
import { useSearchParamState } from '@prophecy/utils/react/hooks';
import { caseInsensitiveInclude } from '@prophecy/utils/string';
import { SlidingDetailsDialog } from 'frontend/core/src/generic-components/Logger/SlidingDetailsDialog';
import { useMemo, useState } from 'react';
import { format as timeAgoFormat } from 'timeago.js';

import { castArray } from '../../../data/util';
import { SQLPipelineObservationLayout } from './Layout';
import { StyledInput, StyledSelect } from './styled';
import { TABS } from './tabs';
import { getRunStatusBadge, withEllipsis } from './utils';

function useDeployedProjects({ fabricId, projectIds }: { fabricId?: string; projectIds?: string[] }) {
  const { fabrics, fabricsMap, isLoading: isJWTLoading } = useOrchestrationFabrics();
  const { getDeployedProjects } = useOrchestrationHistoryApis();

  const { data: deployedProjects, isInitialLoading: isLoading } = useOrchestrationMultiFabricQuery(
    ['orchestration-deployed-projects', fabricId, projectIds],
    () =>
      getDeployedProjects({
        fabricIds: fabricId ? [fabricId] : undefined,
        projectIds
      })
  );

  return {
    deployedProjects: castArray(deployedProjects?.data.projects),
    isLoading: isLoading || isJWTLoading,
    fabrics,
    fabricsMap
  };
}

export function DeployedProjects() {
  const [fabricId, setFabricId] = useSearchParamState('dpFabricId');
  const [projectName, setProjectName] = useSearchParamState('dpProjectName');
  const { isLoading: isProjectsLoading, projects, projectsMap } = useUserSQLProjects();
  const projectIds = useMemo(() => {
    return projects
      .filter((project) => caseInsensitiveInclude(project.name, projectName))
      .map((project) => project._id);
  }, [projectName, projects]);
  const noMatchingProjects = projectIds.length === 0 && projectName;
  const [errorMessageToShow, setErrorMessageToShow] = useState<string | null>(null);

  const {
    deployedProjects,
    isLoading: isDeployedProjectsLoading,
    fabrics,
    fabricsMap
  } = useDeployedProjects({
    fabricId,
    projectIds
  });

  const isVerticalApp = useVerticalAppFlag();

  return (
    <SQLPipelineObservationLayout title='Deployed Projects' count={deployedProjects.length}>
      <Stack direction='horizontal' gap={theme.spaces.x20} alignY='end'>
        <StyledInput
          placeholder='Search project'
          icon={<SearchSmIcon type='default' />}
          value={projectName}
          onChange={(value) => setProjectName(value || undefined)}
          iconPlacement='left'
          data-test-id='sql-deployed-projects-search-input'
        />
        {!isVerticalApp && (
          <StyledSelect
            label='Fabric'
            allowClear
            placeholder='Select Fabric'
            options={fabrics.map((fabric) => ({
              label: fabric.name,
              value: fabric.id
            }))}
            value={fabricId}
            onChange={setFabricId}
            data-test-id='sql-deployed-projects-fabric-select'
          />
        )}
      </Stack>
      <Table
        loading={isDeployedProjectsLoading || isProjectsLoading}
        virtualize
        placeholderRows={50}
        height='100%'
        width='100%'
        fixedHeader
        data-test-id='sql-deployed-projects-table'
        columns={[
          {
            title: 'Project',
            render: (_, record) => {
              return (
                <StyledTableLink
                  to={
                    isVerticalApp
                      ? getSqlIDEUrl({ uid: record.projectId })
                      : Private_Routes.Project.home.getUrl({ uid: record.projectId })
                  }
                  data-test-id={`sql-deployed-projects-${record.projectId}-link`}>
                  {projectsMap.get(record.projectId) || record.projectId}
                </StyledTableLink>
              );
            },
            key: 'projectId'
          },
          {
            title: 'Fabric',
            render: (_, record) => {
              const fabric = fabricsMap.get(record.fabricId);
              return withEllipsis(fabric?.name || record.fabricId);
            },
            key: 'fabric'
          },
          {
            title: 'Release Version',
            render: (_, record) => record.releaseVersion,
            key: 'releaseVersion'
          },
          {
            title: 'Published',
            render: (_, record) => {
              const published = new Date(record.published);
              return timeAgoFormat(published);
            },
            key: 'published'
          },
          {
            title: 'Last Run',
            render: (_, record) => {
              if (!record.latestRun) {
                return 'N/A';
              }
              return timeAgoFormat(new Date(record.latestRun.createdAt));
            },
            key: 'lastRun'
          },
          {
            title: 'Last Run Status',
            render: (_, record) => {
              if (!record.latestRun) {
                return 'N/A';
              }

              return getRunStatusBadge(record.latestRun.runStatus, record.latestRun.errorMessage, () =>
                setErrorMessageToShow(record.latestRun?.errorMessage || '')
              );
            },
            key: 'result'
          },
          {
            title: '',
            key: 'actions',
            width: '50px',
            align: 'center',
            render: (_, record) => {
              if (isVerticalApp) {
                return null;
              }
              return (
                <StyledTableLink
                  tooltip='More Details'
                  to={Private_Routes.IDE_Home.getUrl(
                    { entity: Entity.Observation.toLowerCase() },
                    {
                      observationTab: TABS.SCHEDULED_PIPELINES,
                      spFabricId: record.fabricId,
                      spProjectId: record.projectId
                    }
                  )}
                  data-test-id={`sql-deployed-projects-more-details-link-${record.projectId}-button`}>
                  <ArrowUpRightIcon type='default' />
                </StyledTableLink>
              );
            }
          }
        ]}
        dataSource={noMatchingProjects ? [] : deployedProjects}
      />
      {errorMessageToShow && (
        <SlidingDetailsDialog
          title='Error Details'
          onClose={() => setErrorMessageToShow(null)}
          message={errorMessageToShow}
          type='error'
        />
      )}
    </SQLPipelineObservationLayout>
  );
}
